import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Tabs,
  Tab,
  Table,
  Badge,
  Form,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faListAlt,
  faUserFriends,
  faTimes,
  faBuilding,
  faHandshake,
  faEnvelope,
  faBell
} from '@fortawesome/free-solid-svg-icons';
import api from 'services/api';
import { toast } from 'react-toastify';

const ParticipantsSummaryModal = ({
  show,
  onHide,
  partnerParticipants,
  companyParticipants,
  salonPartners,
  salonCompanies,
  onOpenWishes,
  salon
}) => {
  const [activeTab, setActiveTab] = useState('partners');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPartnerParticipants, setFilteredPartnerParticipants] =
    useState([]);
  const [filteredCompanyParticipants, setFilteredCompanyParticipants] =
    useState([]);

  // Appliquer le filtre de recherche lorsque le terme ou les participants changent
  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();

      // Filtrer les participants des partenaires
      setFilteredPartnerParticipants(
        partnerParticipants.filter(
          participant =>
            participant.firstname?.toLowerCase().includes(term) ||
            participant.lastname?.toLowerCase().includes(term) ||
            salonPartners
              .find(p => p.id === participant.partner_id)
              ?.name?.toLowerCase()
              .includes(term)
        )
      );

      // Filtrer les participants des sociétés
      setFilteredCompanyParticipants(
        companyParticipants.filter(
          participant =>
            participant.firstname?.toLowerCase().includes(term) ||
            participant.lastname?.toLowerCase().includes(term) ||
            salonCompanies
              .find(c => c.id === participant.company_id)
              ?.name?.toLowerCase()
              .includes(term)
        )
      );
    } else {
      // Sans filtre, on affiche tous les participants
      setFilteredPartnerParticipants(partnerParticipants);
      setFilteredCompanyParticipants(companyParticipants);
    }
  }, [
    searchTerm,
    partnerParticipants,
    companyParticipants,
    salonPartners,
    salonCompanies
  ]);

  // Trouver le nom de l'entité (partenaire ou société) associée à un participant
  const getEntityName = (participant, isPartner) => {
    if (isPartner) {
      return (
        salonPartners.find(p => p.id === participant.partner_id)?.name || '?'
      );
    } else {
      return (
        salonCompanies.find(c => c.id === participant.company_id)?.name || '?'
      );
    }
  };

  // Obtenir le compteur de vœux pour un participant
  const getWishesCount = participant => {
    return participant.wishes_count || 0;
  };

  const handleSendPlanningLink = async (participant, type) => {
    try {
      const response = await api.post(
        `/admin/partner-salons/${salon.id}/participants/${type}/${participant.id}/send-planning-link`
      );
      if (response.data.success) {
        toast(response.data.message);
      } else {
        toast(response.data.message || 'Une erreur est survenue.');
      }
    } catch (error) {
      toast(
        error.response?.data?.message || "Erreur lors de l'envoi de l'email."
      );
    }
  };

  const handleSendPlanningToAll = async () => {
    try {
      const response = await api.post(
        `/admin/partner-salons/${salon.id}/send-planning-to-all`
      );
      if (response.data.success) {
        toast(response.data.message);
      } else {
        toast(response.data.message || 'Une erreur est survenue.');
      }
    } catch (error) {
      toast(
        error.response?.data?.message || "Erreur lors de l'envoi des plannings."
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faUserFriends} className="me-2" />
          Récapitulatif des participants
        </Modal.Title>
        <div className="ms-auto">
          {(() => {
            const today = new Date();
            const registrationStart = salon?.registration_start_date
              ? new Date(salon.registration_start_date)
              : null;
            const registrationEnd = salon?.registration_end_date
              ? new Date(salon.registration_end_date)
              : null;
            const isInRegistrationPeriod =
              registrationStart &&
              registrationEnd &&
              today >= registrationStart &&
              today <= registrationEnd;

            return isInRegistrationPeriod ? (
              <Button variant="outline-secondary" size="sm" className="me-2">
                <FontAwesomeIcon icon={faBell} className="me-1" />
                Relancer tous les responsables
              </Button>
            ) : null;
          })()}
          {salon?.is_planning_finalized ? (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleSendPlanningToAll}
            >
              <FontAwesomeIcon icon={faEnvelope} className="me-1" />
              Envoyer les plannings
            </Button>
          ) : null}
        </div>
      </Modal.Header>
      <Modal.Body>
        {/* Barre de recherche */}
        <InputGroup className="mb-3">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Rechercher un participant ou une entité..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <Button
              variant="outline-secondary"
              onClick={() => setSearchTerm('')}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          )}
        </InputGroup>

        {/* Statistiques générales */}
        <div className="d-flex justify-content-between mb-3">
          <div className="text-muted">
            <strong>{partnerParticipants.length}</strong> participants de
            partenaires |<strong> {companyParticipants.length}</strong>{' '}
            participants d'entreprises
          </div>
        </div>

        {/* Onglets pour séparer les types de participants */}
        <Tabs
          activeKey={activeTab}
          onSelect={k => setActiveTab(k)}
          className="mb-3"
          fill
        >
          <Tab
            eventKey="partners"
            title={
              <span>
                <FontAwesomeIcon icon={faHandshake} className="me-2" />
                Participants des partenaires (
                {filteredPartnerParticipants.length})
              </span>
            }
          >
            <Table striped responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Participant</th>
                  <th>Partenaire</th>
                  <th className="text-center">Vœux</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPartnerParticipants.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-3">
                      Aucun participant trouvé
                    </td>
                  </tr>
                ) : (
                  filteredPartnerParticipants.map(participant => (
                    <tr key={`partner-${participant.id}`}>
                      <td>
                        {participant.firstname} {participant.lastname}
                      </td>
                      <td>{getEntityName(participant, true)}</td>
                      <td className="text-center">
                        <Badge
                          bg={
                            getWishesCount(participant) > 0
                              ? 'success'
                              : 'warning'
                          }
                        >
                          {getWishesCount(participant)} vœux
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => onOpenWishes(participant, 'partner')}
                        >
                          <FontAwesomeIcon icon={faListAlt} className="me-1" />
                          Vœux
                        </Button>
                        {salon?.is_planning_finalized ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="ms-2"
                            onClick={() =>
                              handleSendPlanningLink(participant, 'partner')
                            }
                            title="Envoyer le lien du planning par email"
                          >
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              className="me-1"
                            />
                            Envoyer planning
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Tab>
          <Tab
            eventKey="companies"
            title={
              <span>
                <FontAwesomeIcon icon={faBuilding} className="me-2" />
                Participants des entreprises (
                {filteredCompanyParticipants.length})
              </span>
            }
          >
            <Table striped responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Participant</th>
                  <th>Entreprise</th>
                  <th className="text-center">Vœux</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanyParticipants.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-3">
                      Aucun participant trouvé
                    </td>
                  </tr>
                ) : (
                  filteredCompanyParticipants.map(participant => (
                    <tr key={`company-${participant.id}`}>
                      <td>
                        {participant.firstname} {participant.lastname}
                      </td>
                      <td>{getEntityName(participant, false)}</td>
                      <td className="text-center">
                        <Badge
                          bg={
                            getWishesCount(participant) > 0
                              ? 'success'
                              : 'warning'
                          }
                        >
                          {getWishesCount(participant)} vœux
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => onOpenWishes(participant, 'company')}
                        >
                          <FontAwesomeIcon icon={faListAlt} className="me-1" />
                          Vœux
                        </Button>
                        {salon?.is_planning_finalized ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="ms-2"
                            onClick={() =>
                              handleSendPlanningLink(participant, 'company')
                            }
                            title="Envoyer le lien du planning par email"
                          >
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              className="me-1"
                            />
                            Envoyer planning
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ParticipantsSummaryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  partnerParticipants: PropTypes.array,
  companyParticipants: PropTypes.array,
  salonPartners: PropTypes.array,
  salonCompanies: PropTypes.array,
  onOpenWishes: PropTypes.func.isRequired,
  salon: PropTypes.object
};

ParticipantsSummaryModal.defaultProps = {
  partnerParticipants: [],
  companyParticipants: [],
  salonPartners: [],
  salonCompanies: []
};

export default ParticipantsSummaryModal;
